/**
 * Task 10: SKU Layer as Alternative View
 * Feature hook for SKU Overhead display mode - a full alternative view
 * that combines overhead view with SKU layer automatically enabled
 */
import { DisplayMode } from '@rta/rta-blueprint-component';
import { Tag } from 'lucide-react';

import { ControllerDisplayModeSupport } from './types';
import { lang } from '@lang';
import { useDesign } from '@hooks';
import { useSubscription } from '@rta/rta-blueprint-library';
import { Feature, FeatureHoookParams } from '..';

type DisplaySkuOverheadModeFeatureParams = FeatureHoookParams;

export const useDisplaySkuOverheadModeFeature = (_params: DisplaySkuOverheadModeFeatureParams = {}): Feature<boolean> => {
	const design = useDesign();
	const controller = design[0] as (typeof design[0]) & ControllerDisplayModeSupport;

	const controllerSupportsFeature = (controller?.navigation?.displayMode ?? null) !== null;
	const inSkuOverheadMode = controller?.navigation?.displayMode === DisplayMode.SkuOverhead || controller?.navigation?.displayMode === DisplayMode.SkuOverheadManufacturing;

	useSubscription(controller, false, [['navigation-display-mode', '*']]);

	const [disabled, tooltip] = (() => {
		if (!controllerSupportsFeature) return [true, lang.navigation.tooltip.disabledNotSupportCurrentView];
		return [false, lang.navigation.tooltip.skuOverheadMode ?? 'SKU Overhead View'];
	})();

	return {
		name: 'sku-overhead-mode',
		label: lang.navigation.label.skuOverheadMode ?? 'SKU View',
		shortcut: '',
		tooltip,
		icon: (props = {}) => <Tag color={inSkuOverheadMode ? 'green' : 'black'} {...props} />,
		disabled,
		value: inSkuOverheadMode,
		hidden: false,
		handler: () => {
			if (controllerSupportsFeature) {
				if (inSkuOverheadMode) {
					// Revert to the appropriate non-SKU mode
					const isManufacturing = controller.navigation.displayMode === DisplayMode.SkuOverheadManufacturing;
					controller.navigation.displayMode = isManufacturing ? DisplayMode.OverheadManufacturing : DisplayMode.Overhead;
				} else {
					// Switch to SKU overhead mode (default to manufacturing view)
					controller.navigation.displayMode = DisplayMode.SkuOverheadManufacturing;
				}
			}
		}
	};
};
